import { createRef } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import { useIntersectionTracker } from "@hooks/useIntersectionTracker";
import type {
  Media,
  MixedCarouselComponentProps,
} from "@services/wordpress/wordpress.types";
import Icons from "@components/icons.ts";
import { ScrollSnapCarouselContainer, ScrollSnapCarouselItem } from "../ScrollSnapCarousel";
import { FadeIn } from "../FadeIn";

interface Props extends Omit<MixedCarouselComponentProps, "slides"> {
  slides: Array<
    Omit<
      {
        quote: string;
        attributed: string;
        attributed_title: string;
        media: number;
      },
      "media"
    > & { media: Media }
  >;
}

export const MixedCarouselDisplay = ({
  acf_fc_layout,
  slides: items,
}: Props & { index: number }) => {
  const [currentItem, setCurrentItem] = useState(0);
  const container = useRef<HTMLDivElement>(null);
  const itemsRef = useRef(
    Array.from({ length: items?.length }, (_) => createRef()),
  );

  const activateSignal = (intersectingIndex: Number) => {
    itemsRef.current.forEach((_, i) => {
      if (i === intersectingIndex) {
        setCurrentItem(i);
      }
    });
  };

  const listRef = useRef<HTMLElement>(null);

  const theInObHook = useIntersectionTracker(
    {
      targetRef: itemsRef,
      root: listRef.current,
      threshold: 0.6,
    },
    activateSignal,
  );

  useEffect(() => {
    theInObHook;
  }, []);

  const handleNextClick = () => {
    const index = currentItem + 1;
    if (currentItem < items.length - 1) {
      setCurrentItem(index);
      if (container.current) {
        container.current.style.scrollBehavior = "smooth";
        const snapLengthX = Math.round(
          container.current.scrollWidth / items.length,
        );
        container.current.scrollLeft = snapLengthX * index;
      }
    }
  };

  const handlePrevClick = () => {
    const index = currentItem - 1;
    if (currentItem > 0) {
      setCurrentItem(index);
      if (container.current) {
        container.current.style.scrollBehavior = "smooth";
        const snapLengthX = Math.round(
          container.current.scrollWidth / items.length,
        );
        container.current.scrollLeft = snapLengthX * index;
      }
    }
  };

  return (
    <section
      data-component={acf_fc_layout}
      class="flex flex-col lg:flex-row mb-16 border-t-gt-black border-b-gt-black border-b-solid border-t-solid border-b border-t"
    >
      <div class="bg-gt-lilac lg:w-2/4 order-2 lg:order-1 relative">
        <ScrollSnapCarouselContainer ref={container}>
          {items.map((_data, index) => {
            return (
              <ScrollSnapCarouselItem
                key={index}
                ref={itemsRef.current[index]}
              >
                <blockquote class="w-full mx-0 px-4 lg:px-0">
                  <div class="w-full flex flex-col">
                    <p class="text-[3.5rem] md:text-[4rem]">“</p>
                    <p class="text-2xl md:text-[2rem]">{_data.quote}</p>
                  </div>
                  <div class="w-full flex flex-col mt-4 mb-6">
                    <cite class="text-[1.125rem] font-bold not-italic">
                      {_data.attributed}
                    </cite>
                    <p>{_data.attributed_title}</p>
                  </div>
                </blockquote>
              </ScrollSnapCarouselItem>
            );
          })}
        </ScrollSnapCarouselContainer>
        <div class="Slider__controls absolute bottom-8 lg:bottom-16 right-8 lg:right-16 z-20 flex justify-end items-center gap-8">
          <button
            class="m-0 flex cursor-pointer items-center justify-center border border-solid border-gt-black p-2 md:p-4 text-3xl rounded bg-gt-lilac"
            onClick={handlePrevClick}
            aria-label="Previous slide"
          >
            <Icons.IconLeft />
          </button>
          <button
            class="m-0 flex cursor-pointer items-center justify-center p-2 md:p-4 border border-solid border-gt-black text-3xl rounded bg-gt-lilac"
            onClick={handleNextClick}
            aria-label="Next slide"
          >
            <Icons.IconRight />
          </button>
        </div>
      </div>
      <div class="basis-1/2 order-1 lg:order-2 aspect-square lg:border-l lg:border-l-solid lg:border-l-gt-black">
        <div class="h-full overflow-x-scroll w-full flex">
          {items.map((slide, index) => {
            return (
              <FadeIn
                animate={index === currentItem}
                key={`fade-carousel${index}`}
              >
                {slide?.media?.media_type === "image" && (
                  <img
                    class="object-cover w-full h-full rounded-md"
                    src={slide.media.source_url}
                    alt={slide.media.alt_text}
                  />
                )}
                {slide?.media?.mime_type.includes("video") && (
                  <video
                    class="object-cover w-full h-full rounded-md"
                    src={slide?.media?.source_url}
                    alt={slide?.media?.alt_text}
                    autoPlay
                    loop
                    muted
                  />
                )}
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
};
